import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ArrowRight } from 'lucide-react';
import { pricing } from '../data/content';
import { Button } from './ui/button';

export const PricingSection = () => {
  return (
    <section data-testid="pricing-section" className="section-spacing bg-gradient-to-b from-orange-50 to-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-coral/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange/5 rounded-full blur-3xl" />
      
      <div className="section-container relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-semibold mb-4">
            Affordable Rates
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Outfit']">
            Transparent <span className="text-coral">Pricing</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Simple, all-inclusive rates. No hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* NSFAS Rate */}
          <div
            data-testid="pricing-nsfas"
            className="price-tag p-1"
          >
            <div className="bg-white rounded-xl p-8 h-full">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-6 h-6 text-coral" />
                <span className="text-sm font-semibold text-coral uppercase tracking-wide">
                  NSFAS Accredited
                </span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Outfit']">
                {pricing.nsfas.label}
              </h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-slate-900 font-['Outfit']">
                  {pricing.nsfas.rate}
                </span>
                <span className="text-slate-500 ml-2">{pricing.nsfas.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'All utilities included',
                  'Free WiFi access',
                  'Access to all amenities',
                  '24/7 Security',
                  'Study rooms'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-700">
                    <div className="w-5 h-5 bg-coral/20 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-coral" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button
                  data-testid="pricing-nsfas-apply-btn"
                  className="w-full bg-gradient-to-r from-coral to-orange hover:from-coral-dark hover:to-coral text-white rounded-full py-6 font-semibold shadow-lg shadow-coral/25"
                >
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Bursary/Private Rate */}
          <div
            data-testid="pricing-bursary"
            className="border-2 border-orange-200 hover:border-coral/50 rounded-2xl p-8 bg-white transition-all shadow-sm hover:shadow-lg"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                Premium Option
              </span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Outfit']">
              {pricing.bursaryPrivate.label}
            </h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-slate-900 font-['Outfit']">
                {pricing.bursaryPrivate.rate}
              </span>
              <span className="text-slate-500 ml-2">{pricing.bursaryPrivate.period}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                'All utilities included',
                'Free WiFi access',
                'Access to all amenities',
                '24/7 Security',
                'Study rooms',
                'Priority room selection'
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-slate-700">
                  <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/contact">
              <Button
                data-testid="pricing-bursary-apply-btn"
                variant="outline"
                className="w-full border-2 border-coral text-coral hover:bg-coral hover:text-white rounded-full py-6 font-semibold"
              >
                Apply Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
